import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Rocket, AlertCircle } from 'lucide-react';
import Cookies from 'js-cookie';
import { getApiUrl } from '@/config/api';
import { toast } from '@/hooks/use-toast';

const RestaurantMenuPublish = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isPublishing, setIsPublishing] = useState(true);
  const [publishProgress, setPublishProgress] = useState(0);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [menuUrl, setMenuUrl] = useState<string | null>(null);

  // Get menu_id from URL params
  const menuId = searchParams.get('menu_id') || 'test123';

  useEffect(() => {
    const publishMenu = async () => {
      try {
        // Get token for authentication
        const token = Cookies.get('auth_token');
        if (!token) {
          toast({
            title: "Authentication Required",
            description: "Please log in to publish menu",
            variant: "destructive"
          });
          navigate('/restaurant-login');
          return;
        }
        
        // Start progress animation
        const progressInterval = setInterval(() => {
          setPublishProgress((prev) => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90; // Stop at 90% until API call completes
            }
            return prev + 10;
          });
        }, 200);

        // Get restaurant ID for the current user
        const userResponse = await fetch(getApiUrl(`menus/${menuId}/items`), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!userResponse.ok) {
          throw new Error('Failed to get restaurant information');
        }

        // Call publish API
        const publishResponse = await fetch(getApiUrl(`menus/${menuId}/publish`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            menu_id: menuId,
            restaurant_id: 'restaurant_123' // TODO: Get actual restaurant_id from user context
          })
        });

        if (!publishResponse.ok) {
          const errorData = await publishResponse.json();
          throw new Error(errorData.detail || 'Failed to publish menu');
        }

        const publishData = await publishResponse.json();
        console.log('✅ Menu published successfully:', publishData);
        
        // Set the live menu URL
        setMenuUrl(publishData.menu_url);
        
        // Complete progress and finish publishing
        setPublishProgress(100);
        setTimeout(() => setIsPublishing(false), 800);

      } catch (error) {
        console.error('❌ Error publishing menu:', error);
        setPublishError(error instanceof Error ? error.message : 'Failed to publish menu');
        setIsPublishing(false);
      }
    };

    publishMenu();
  }, [menuId]);

  const handleDashboard = () => {
    // Navigate to restaurant dashboard (placeholder)
    navigate('/restaurant-dashboard');
  };

  const handleViewMenu = () => {
    // Navigate to live menu preview with menu_id only
    navigate(`/menu-preview?menu_id=${menuId}`);
  };

  if (isPublishing) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] relative overflow-hidden flex items-center justify-center">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 text-center"
        >
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-12 border border-white/20 max-w-md mx-auto">
            <Rocket className="h-16 w-16 text-yellow-400 mx-auto mb-6 animate-bounce" />
            <h2 className="text-3xl font-bold text-white mb-4">Publishing Your Menu</h2>
            <p className="text-white/80 mb-8">Setting up your AI-powered insights...</p>
            
            <div className="mb-6">
              <div className="bg-white/10 rounded-full h-4 overflow-hidden">
                <motion.div
                  initial={{ width: '0%' }}
                  animate={{ width: `${publishProgress}%` }}
                  className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500"
                  transition={{ duration: 0.3 }}
                />
              </div>
              <p className="text-white/60 mt-2">{publishProgress}%</p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (publishError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] relative overflow-hidden flex items-center justify-center">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 text-center max-w-md mx-auto"
        >
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mb-6"
            >
              <AlertCircle className="h-8 w-8 text-red-400" />
            </motion.div>
            
            <h1 className="text-2xl font-bold text-white mb-4">
              Publishing Failed
            </h1>
            <p className="text-white/80 mb-6">
              {publishError}
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 font-medium rounded-lg hover:from-yellow-500 hover:to-yellow-600 transition-all"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/restaurant-menu-review')}
                className="flex-1 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg hover:bg-white/20 transition-all"
              >
                Back to Review
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] relative flex flex-col">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-yellow-400/10 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="max-w-2xl mx-auto text-center"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
            className="inline-flex items-center justify-center w-24 h-24 bg-green-500/20 rounded-full mb-8"
          >
            <Check className="h-12 w-12 text-green-400" />
          </motion.div>

          {/* Celebration Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h1 className="text-5xl font-bold text-white mb-6">
              Congratulations! 🎉
            </h1>
            <p className="text-2xl text-white/90 mb-8">
              Your account is ready!
            </p>
            <p className="text-lg text-white/70 max-w-xl mx-auto mb-12">
              Your restaurant is now set up and ready to go. Click below to access your dashboard and start managing your business.
            </p>
          </motion.div>

          {/* Action Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mb-12"
          >
            <button
              onClick={handleDashboard}
              className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 font-bold text-lg rounded-xl hover:from-yellow-500 hover:to-yellow-600 transition-all shadow-2xl hover:shadow-yellow-500/50 hover:scale-105"
            >
              Go to Dashboard
              <ArrowRight className="h-6 w-6" />
            </button>
          </motion.div>

          {/* Support Message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <p className="text-white/60 text-sm">
              Need help? Our team is here 24/7. Email{' '}
              <a href="mailto:support@dineai.ai" className="text-yellow-400 hover:text-yellow-300 underline">
                support@dineai.ai
              </a>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default RestaurantMenuPublish;